
import React, { useRef, useState, useEffect } from "react";

const INDUSTRY_OPTIONS = [
  "Travel & Tourism",
  "Airlines",
  "Hotel & Hospitality",
  "Logistics & Transportation",
  "Tour Operators & Agencies",
  "Car Rental Services",
  "Cruise & Shipping",
  "Other",
];

const LOCATION_OPTIONS = ["India", "USA", "UK", "Other"];

const FISCAL_YEAR_OPTIONS = [
  "April - March",
  "Jan - Dec",
];

const BASE_CURRENCY_OPTIONS = [
  "INR",
  "USD",
  "EUR",
];

const LANGUAGES = [
  "English",
  "Hindi",
  "French",
];

const TIMEZONES = [
  "(GMT +5:30) India Standard Time (Asia/Calcutta)",
  "(GMT 0:00) UTC",
];

const DATE_FORMATS = [
  "dd/MM/yyyy",
  "MM/dd/yyyy",
  "yyyy-MM-dd",
];

const STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Other",
];

const DEFAULT_FORM = {
  client_id: null,

  // Organization
  name: "",
  industry: "",
  location: "",
  company_type: "",

  // Address & Contact
  address: "",
  street2: "",
  city: "",
  zipcode: "",
  state: "",
  phone: "",
  email: "",
  website_url: "",

  // Financial & Regional
  base_currency: "INR",
  fiscal_year: "",
  report_basis: "accrual",
  language: "English",
  timezone: TIMEZONES[0],
  date_format: DATE_FORMATS[0],

  // Company / Tax
  company_registration_number: "",
  client_code: "",
  tax_id: "",

  // Documents
  logo: "",
  signature: "",
  seal: "",
};

const inputClass =
  "w-full h-9 rounded border border-gray-300 bg-white px-3 text-sm text-gray-800 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100 placeholder:text-gray-300";

const OrganisationProfileForm = ({ client = {}, onSave }) => {
  const [form, setForm] = useState(DEFAULT_FORM);

  const [logoUrl, setLogoUrl] = useState(null);
  const [signatureUrl, setSignatureUrl] = useState(null);
  const [sealUrl, setSealUrl] = useState(null);

  const fileInput = useRef(null);

  // ============================================================
  // LOAD CLIENT DATA
  // ============================================================
  useEffect(() => {
    if (!client || Object.keys(client).length === 0) {
      setForm(DEFAULT_FORM);
      setLogoUrl(null);
      setSignatureUrl(null);
      setSealUrl(null);
      return;
    }

    let logoString = client.logo || "";

    if (
      logoString &&
      !logoString.startsWith("data:image")
    ) {
      logoString = `data:image/png;base64,${logoString}`;
    }

    let signatureString = client.signature || "";

    if (
      signatureString &&
      !signatureString.startsWith("data:image")
    ) {
      signatureString = `data:image/png;base64,${signatureString}`;
    }

    let sealString = client.seal || "";

    if (
      sealString &&
      !sealString.startsWith("data:image")
    ) {
      sealString = `data:image/png;base64,${sealString}`;
    }

    setForm({
      client_id: client.client_id || null,

      // Organization
      name: client.name || "",
      industry: client.industry || "",
      location: client.location || "",
      company_type: client.company_type || "",

      // Address & Contact
      address: client.address || "",
      street2: client.street2 || "",
      city: client.city || "",
      zipcode: client.zipcode || "",
      state: client.state || "",
      phone: client.phone || "",
      email: client.email || "",
      website_url: client.website_url || "",

      // Financial & Regional
      base_currency: client.base_currency || "INR",
      fiscal_year: client.fiscal_year || "",
      report_basis: client.report_basis || "accrual",
      language: client.language || "English",
      timezone: client.timezone || TIMEZONES[0],
      date_format: client.date_format || DATE_FORMATS[0],

      // Company / Tax
      company_registration_number:
        client.company_registration_number || "",
      client_code: client.client_code || "",
      tax_id: client.tax_id || "",

      // Documents
      logo: logoString,
      signature: signatureString,
      seal: sealString,
    });

    setLogoUrl(logoString || null);
    setSignatureUrl(signatureString || null);
    setSealUrl(sealString || null);
  }, [client]);

  // ============================================================
  // HANDLE CHANGE
  // ============================================================
  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ============================================================
  // REPORT BASIS
  // ============================================================
  const handleReportBasisChange = (e) => {
    setForm((prev) => ({
      ...prev,
      report_basis: e.target.value,
    }));
  };

  // ============================================================
  // IMAGE VALIDATION
  // ============================================================
  const validateImage = (file) => {
    if (!file) return false;

    if (file.size > 1024 * 1024) {
      alert("File too large (maximum 1MB).");
      return false;
    }

    if (!file.type.startsWith("image/")) {
      alert("Please select a valid image file.");
      return false;
    }

    return true;
  };

  // ============================================================
  // LOGO
  // ============================================================
  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];

    if (!validateImage(file)) return;

    const reader = new FileReader();

    reader.onload = (event) => {
      const imageData = event.target.result;

      setLogoUrl(imageData);

      setForm((prev) => ({
        ...prev,
        logo: imageData,
      }));
    };

    reader.readAsDataURL(file);
  };

  // ============================================================
  // SIGNATURE
  // ============================================================
  const handleSignatureChange = (e) => {
    const file = e.target.files?.[0];

    if (!validateImage(file)) return;

    const reader = new FileReader();

    reader.onload = (event) => {
      const imageData = event.target.result;

      setSignatureUrl(imageData);

      setForm((prev) => ({
        ...prev,
        signature: imageData,
      }));
    };

    reader.readAsDataURL(file);
  };

  // ============================================================
  // SEAL
  // ============================================================
  const handleSealChange = (e) => {
    const file = e.target.files?.[0];

    if (!validateImage(file)) return;

    const reader = new FileReader();

    reader.onload = (event) => {
      const imageData = event.target.result;

      setSealUrl(imageData);

      setForm((prev) => ({
        ...prev,
        seal: imageData,
      }));
    };

    reader.readAsDataURL(file);
  };

  // ============================================================
  // SUBMIT
  // ============================================================
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) {
      alert("Please enter Organization Name.");
      return;
    }

    if (!form.location) {
      alert("Please select Organization Location.");
      return;
    }

    if (!form.email.trim()) {
      alert("Please enter Email.");
      return;
    }

    if (onSave) {
      try {
        await onSave(form);
      } catch (err) {
        console.error("Error while saving:", err);
      }
    }
  };

  return (
    <div className="w-full max-w-5xl bg-white rounded-lg shadow-md my-6 ml-0 overflow-hidden">

      {/* ========================================================
          HEADER
      ========================================================= */}
      <div className="px-6 py-4 border-b border-gray-200">

        <div className="flex items-center justify-between">

          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Organization Profile
            </h2>

            <p className="text-xs text-gray-400 mt-0.5">
              Manage organization information and settings
            </p>
          </div>

          {form.client_code && (
            <span className="px-3 py-1.5 rounded bg-gray-50 border border-gray-200 text-gray-600 font-mono text-xs">
              ID: {form.client_code}
            </span>
          )}

        </div>
      </div>

      {/* ========================================================
          FORM
      ========================================================= */}
      <form
        onSubmit={handleSubmit}
        className="px-6 py-5"
      >

        {/* ======================================================
            01 ORGANIZATION INFORMATION
        ======================================================= */}
        <SectionTitle
          number="01"
          title="Organization Information"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3">

          <Field
            label="Organization Name"
            required
          >
            <input
              id="name"
              name="name"
              type="text"
              required
              value={form.name}
              onChange={handleChange}
              placeholder="Organization name"
              className={inputClass}
            />
          </Field>

          <Field label="Industry">
            <select
              name="industry"
              value={form.industry}
              onChange={handleChange}
              className={inputClass}
            >
              <option value="">
                Select Industry
              </option>

              {INDUSTRY_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </Field>

          <Field
            label="Organization Location"
            required
          >
            <select
              name="location"
              required
              value={form.location}
              onChange={handleChange}
              className={inputClass}
            >
              <option value="">
                Select Location
              </option>

              {LOCATION_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Company Type">
            <input
              name="company_type"
              type="text"
              value={form.company_type}
              onChange={handleChange}
              placeholder="Example: Private Limited"
              className={inputClass}
            />
          </Field>

        </div>

        {/* ======================================================
            02 ADDRESS & CONTACT
        ======================================================= */}
        <SectionTitle
          number="02"
          title="Address & Contact"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3">

          {/* Address - Full Width */}
          <div className="md:col-span-2">

            <Field label="Organization Address">

              <input
                name="address"
                type="text"
                value={form.address}
                onChange={handleChange}
                placeholder="Street, Block, Area"
                className={inputClass}
              />

            </Field>

          </div>

          <Field label="Street 2">
            <input
              name="street2"
              type="text"
              value={form.street2}
              onChange={handleChange}
              placeholder="Apartment, building, landmark"
              className={inputClass}
            />
          </Field>

          <Field label="City">
            <input
              name="city"
              type="text"
              value={form.city}
              onChange={handleChange}
              placeholder="City"
              className={inputClass}
            />
          </Field>

          <Field label="State">
            <select
              name="state"
              value={form.state}
              onChange={handleChange}
              className={inputClass}
            >
              <option value="">
                Select State
              </option>

              {STATES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </Field>

          <Field label="PIN Code">
            <input
              name="zipcode"
              type="text"
              maxLength={10}
              value={form.zipcode}
              onChange={handleChange}
              placeholder="PIN Code"
              className={inputClass}
            />
          </Field>

          <Field label="Phone">
            <input
              name="phone"
              type="tel"
              value={form.phone}
              onChange={handleChange}
              placeholder="Phone number"
              className={inputClass}
            />
          </Field>

          <Field
            label="Email"
            required
          >
            <input
              name="email"
              type="email"
              required
              value={form.email}
              onChange={handleChange}
              placeholder="email@example.com"
              autoComplete="email"
              className={inputClass}
            />
          </Field>

          <div className="md:col-span-2">

            <Field label="Website URL">

              <input
                name="website_url"
                type="url"
                value={form.website_url}
                onChange={handleChange}
                placeholder="https://example.com"
                className={inputClass}
              />

            </Field>

          </div>

        </div>

        {/* ======================================================
            03 FINANCIAL & REGIONAL SETTINGS
        ======================================================= */}
        <SectionTitle
          number="03"
          title="Financial & Regional Settings"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3">

          <Field label="Base Currency">
            <select
              name="base_currency"
              value={form.base_currency}
              onChange={handleChange}
              className={inputClass}
            >
              {BASE_CURRENCY_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Fiscal Year">
            <select
              name="fiscal_year"
              value={form.fiscal_year}
              onChange={handleChange}
              className={inputClass}
            >
              <option value="">
                Select Fiscal Year
              </option>

              {FISCAL_YEAR_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </Field>

          {/* Report Basis */}
          <Field label="Report Basis">

            <div className="h-9 flex items-center gap-6">

              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">

                <input
                  type="radio"
                  name="report_basis"
                  value="accrual"
                  checked={form.report_basis === "accrual"}
                  onChange={handleReportBasisChange}
                  className="w-3.5 h-3.5"
                />

                Accrual

              </label>

              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">

                <input
                  type="radio"
                  name="report_basis"
                  value="cash"
                  checked={form.report_basis === "cash"}
                  onChange={handleReportBasisChange}
                  className="w-3.5 h-3.5"
                />

                Cash

              </label>

            </div>

          </Field>

          <Field label="Language">
            <select
              name="language"
              value={form.language}
              onChange={handleChange}
              className={inputClass}
            >
              {LANGUAGES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Time Zone">
            <select
              name="timezone"
              value={form.timezone}
              onChange={handleChange}
              className={inputClass}
            >
              {TIMEZONES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Date Format">
            <select
              name="date_format"
              value={form.date_format}
              onChange={handleChange}
              className={inputClass}
            >
              {DATE_FORMATS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </Field>

        </div>

        {/* ======================================================
            04 COMPANY & TAX
        ======================================================= */}
        <SectionTitle
          number="04"
          title="Company & Tax Information"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3">

          <Field label="Company Registration Number">
            <input
              name="company_registration_number"
              type="text"
              value={form.company_registration_number}
              onChange={handleChange}
              placeholder="Registration number"
              className={inputClass}
            />
          </Field>

          <Field label="Tax ID">
            <input
              name="tax_id"
              type="text"
              value={form.tax_id}
              onChange={handleChange}
              placeholder="Tax ID / GST / VAT"
              className={inputClass}
            />
          </Field>

        </div>

        {/* ======================================================
            05 DOCUMENTS & BRANDING
        ======================================================= */}
        <SectionTitle
          number="05"
          title="Documents & Branding"
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

          {/* LOGO */}
          <UploadBox
            title="Organization Logo"
            inputId="logo"
            imageUrl={logoUrl}
            imageAlt="Organization Logo"
            onChange={handleLogoChange}
            type="logo"
            helper="Max 1MB"
            inputRef={fileInput}
          />

          {/* SIGNATURE */}
          <UploadBox
            title="Digital Signature"
            inputId="signature"
            imageUrl={signatureUrl}
            imageAlt="Digital Signature"
            onChange={handleSignatureChange}
            type="signature"
            helper="PNG preferred, max 1MB"
          />

          {/* SEAL */}
          <UploadBox
            title="Company Seal"
            inputId="seal"
            imageUrl={sealUrl}
            imageAlt="Company Seal"
            onChange={handleSealChange}
            type="seal"
            helper="Round PNG preferred, max 1MB"
          />

        </div>

        {/* ======================================================
            SAVE
        ======================================================= */}
        <div className="mt-6 pt-4 border-t border-gray-200 flex items-center justify-between">

          <p className="text-xs text-gray-400">
            <span className="text-red-500">*</span> Required fields
          </p>

          <button
            type="submit"
            className="px-8 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-200 transition"
          >
            Save Changes
          </button>

        </div>

      </form>
    </div>
  );
};

// ================================================================
// SECTION TITLE
// ================================================================
const SectionTitle = ({
  number,
  title,
}) => {
  return (
    <div className="mt-6 mb-3 pb-1.5 border-b border-gray-200">

      <div className="flex items-center gap-2">

        <span className="flex items-center justify-center w-6 h-6 rounded bg-blue-50 text-blue-600 text-[10px] font-semibold">
          {number}
        </span>

        <h3 className="text-sm font-semibold text-gray-800">
          {title}
        </h3>

      </div>

    </div>
  );
};

// ================================================================
// FIELD
// ================================================================
const Field = ({
  label,
  required = false,
  children,
}) => {
  return (
    <div>

      <label className="block text-xs font-medium text-gray-500 mb-1">
        {label}

        {required && (
          <span className="text-red-500 ml-1">
            *
          </span>
        )}
      </label>

      {children}

    </div>
  );
};

// ================================================================
// UPLOAD BOX
// ================================================================
const UploadBox = ({
  title,
  inputId,
  imageUrl,
  imageAlt,
  onChange,
  type,
  helper,
  inputRef,
}) => {
  const isSeal = type === "seal";

  return (
    <div className="border border-gray-200 rounded-md p-3">

      <div className="flex items-center justify-between mb-2">

        <h4 className="text-xs font-semibold text-gray-700">
          {title}
        </h4>

        <span className="text-[10px] text-gray-400">
          {helper}
        </span>

      </div>

      <div className="flex items-center gap-4">

        <label
          htmlFor={inputId}
          className={`
            ${
              isSeal
                ? "w-20 h-20 rounded-full"
                : "w-28 h-20 rounded"
            }
            border-2 border-dashed border-gray-300
            flex items-center justify-center
            bg-gray-50 cursor-pointer
            hover:bg-gray-100 hover:border-blue-300
            overflow-hidden transition
          `}
          title={`Click to upload ${title}`}
        >

          {imageUrl ? (
            <img
              src={imageUrl}
              alt={imageAlt}
              className={`
                ${
                  isSeal
                    ? "w-20 h-20 rounded-full"
                    : "w-28 h-20"
                }
                object-contain
              `}
              onError={(e) => {
                e.currentTarget.onerror = null;

                if (type === "logo") {
                  e.currentTarget.src = "/default-logo.png";
                }
              }}
            />
          ) : (
            <span className="text-[10px] text-gray-400 text-center">
              Click to
              <br />
              Upload
            </span>
          )}

          <input
            id={inputId}
            type="file"
            accept="image/*"
            className="hidden"
            ref={inputRef}
            onChange={onChange}
          />

        </label>

        <div className="text-[10px] text-gray-400 leading-relaxed">
          Click the image area
          <br />
          to select a file.
          <br />
          Maximum size 1MB.
        </div>

      </div>

    </div>
  );
};

export default OrganisationProfileForm;

